import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ArrowRight, ArrowLeft, Info, ChevronRight, ShieldCheck, RefreshCcw, Lightbulb } from 'lucide-react';
import { Button } from './Button';
import { WizardState } from '../types';
import { FixelsHint } from './Fixels';

export const DamageCheck: React.FC = () => {
  const initialState: WizardState = {
    step: 1,
    incidentType: null,
    injuryStatus: null,
    workStatus: null,
    isFinished: false
  };

  const [state, setState] = useState<WizardState>(initialState);

  const nextStep = () => {
    setState(prev => ({ ...prev, step: prev.step + 1 }));
  };

  const prevStep = () => {
    setState(prev => ({ ...prev, step: prev.step - 1 }));
  };

  const resetWizard = () => {
    setState(initialState);
  };

  const selectOption = (key: keyof WizardState, value: any) => {
    setState(prev => ({ ...prev, [key]: value }));
    setTimeout(nextStep, 250); 
  };

  const OptionButton = ({ label, isSelected, onClick }: { label: string, isSelected: boolean, onClick: () => void }) => (
    <button
      onClick={onClick}
      className={`w-full text-left px-5 py-4 sm:px-6 sm:py-5 rounded-xl border transition-all duration-200 flex items-center justify-between group relative overflow-hidden active:scale-[0.99]
        ${isSelected 
          ? 'border-brand-600 bg-brand-50 text-brand-900 ring-1 ring-brand-600 shadow-sm z-10' 
          : 'border-slate-200 bg-white hover:border-brand-300 hover:bg-slate-50 text-slate-700 hover:shadow-sm'
        }`}
    >
      <div className="flex items-center gap-4 relative z-10">
        <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${isSelected ? 'border-brand-600 bg-brand-600' : 'border-slate-300 bg-white'}`}>
           {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
        </div>
        <span className="font-medium text-lg">{label}</span>
      </div>
    </button>
  );

  return (
    <section id="schadecheck" className="py-20 sm:py-24 bg-white relative scroll-mt-16">
      <div className="absolute inset-0 bg-slate-50/50 -z-10 h-full"></div>
      
      <div className="max-w-2xl mx-auto px-4">
        
        <div className="text-center mb-10 sm:mb-12">
          <h2 className="text-3xl font-semibold text-slate-900 mb-3 tracking-tight">Check uw mogelijkheden</h2>
          <p className="text-slate-500 text-lg font-light">3 korte vragen. Direct duidelijkheid.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden relative min-h-[420px] flex flex-col">
          {!state.isFinished && (
            <div className="absolute top-0 left-0 w-full h-1.5 bg-slate-100">
              <motion.div 
                className="h-full bg-brand-600"
                initial={{ width: 0 }}
                animate={{ width: `${((state.step - 1) / 3) * 100}%` }}
                transition={{ duration: 0.5 }}
              ></motion.div>
            </div>
          )}

          <div className="p-6 sm:p-10 md:p-12 flex-1 flex flex-col justify-center">
            <AnimatePresence mode="wait">
              
              {/* Step 1 */}
              {state.step === 1 && (
                <motion.div 
                  key="step1"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <span className="text-xs font-semibold tracking-wider text-slate-400 uppercase mb-3 block">Stap 1 van 3</span>
                  <h3 className="text-2xl font-semibold text-slate-900 mb-8">Wat is er precies gebeurd?</h3>
                  <div className="space-y-3">
                    <OptionButton 
                      label="Verkeersongeval" 
                      isSelected={state.incidentType === 'verkeer'} 
                      onClick={() => selectOption('incidentType', 'verkeer')} 
                    />
                    <OptionButton 
                      label="Bedrijfsongeval" 
                      isSelected={state.incidentType === 'werk'} 
                      onClick={() => selectOption('incidentType', 'werk')} 
                    />
                    <OptionButton 
                      label="Anders (bijv. hondenbeet, val)" 
                      isSelected={state.incidentType === 'anders'} 
                      onClick={() => selectOption('incidentType', 'anders')} 
                    />
                  </div>
                </motion.div>
              )}

              {/* Step 2 */}
              {state.step === 2 && (
                <motion.div 
                  key="step2"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="mb-6">
                    <button onClick={prevStep} className="text-sm font-medium text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors px-0 py-2">
                      <ArrowLeft size={16} /> Vorige stap
                    </button>
                  </div>
                  
                  <h3 className="text-2xl font-semibold text-slate-900 mb-2">Heeft u letsel opgelopen?</h3>
                  <p className="text-slate-500 mb-8 text-base">Denk aan botbreuken, whiplash, littekens of aanhoudende pijnklachten.</p>
                  
                  <div className="space-y-3">
                    <OptionButton 
                      label="Ja, ik heb letsel" 
                      isSelected={state.injuryStatus === 'ja'} 
                      onClick={() => selectOption('injuryStatus', 'ja')} 
                    />
                    <OptionButton 
                      label="Ik weet het niet zeker" 
                      isSelected={state.injuryStatus === 'twijfel'} 
                      onClick={() => selectOption('injuryStatus', 'twijfel')} 
                    />
                    <OptionButton 
                      label="Nee, alleen materiële schade" 
                      isSelected={state.injuryStatus === 'nee'} 
                      onClick={() => selectOption('injuryStatus', 'nee')} 
                    />
                  </div>
                </motion.div>
              )}

              {/* Step 3 */}
              {state.step === 3 && (
                <motion.div 
                  key="step3"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.3 }}
                >
                   <div className="mb-6">
                    <button onClick={prevStep} className="text-sm font-medium text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors px-0 py-2">
                      <ArrowLeft size={16} /> Vorige stap
                    </button>
                  </div>

                  <h3 className="text-2xl font-semibold text-slate-900 mb-8">Wat is de impact op uw werk?</h3>
                  <div className="space-y-3">
                    <OptionButton 
                      label="Ik ben (deels) uitgevallen" 
                      isSelected={state.workStatus === 'nee' || state.workStatus === 'deels'} 
                      onClick={() => setState(prev => ({ ...prev, workStatus: 'deels', step: 4, isFinished: true }))} 
                    />
                    <OptionButton 
                      label="Ik werk (nog) volledig" 
                      isSelected={state.workStatus === 'ja'} 
                      onClick={() => setState(prev => ({ ...prev, workStatus: 'ja', step: 4, isFinished: true }))} 
                    />
                  </div>
                </motion.div>
              )}

              {/* Result */}
              {state.isFinished && (
                <motion.div 
                  key="result"
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                  className="text-center pt-2"
                >
                  
                  {state.injuryStatus === 'nee' ? (
                     <div className="max-w-md mx-auto text-center">
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center text-slate-500 mx-auto mb-6">
                            <Info size={32} />
                        </div>
                        <h3 className="text-xl font-semibold text-slate-900 mb-4">Waarschijnlijk geen letselschadezaak</h3>
                        <p className="text-slate-600 mb-8 leading-relaxed">
                            Onze experts richten zich op slachtoffers met lichamelijk letsel. 
                            Bij alleen blikschade adviseren wij u contact op te nemen met uw autoverzekeraar.
                        </p>
                        <Button variant="outline" onClick={resetWizard}>
                          <RefreshCcw size={16} className="mr-2" />
                          Opnieuw beginnen
                        </Button>
                        
                        {/* Sales Hint added here for negative result too */}
                        <FixelsHint />
                     </div>
                  ) : (
                    <div className="text-left">
                      {/* Success "Golden Ticket" Card - Optimised for Empowerment */}
                      <div className="flex items-start sm:items-center gap-5 mb-8">
                        <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center text-green-600 shrink-0 shadow-sm border border-green-200">
                            <ShieldCheck size={28} strokeWidth={2.5} />
                        </div>
                        <div>
                          <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1 leading-tight">U heeft recht op kosteloze hulp</h3>
                          <p className="text-slate-600 font-medium">Op basis van uw antwoorden kunnen wij u helpen.</p>
                        </div>
                      </div>
                      
                      <div className="bg-gradient-to-br from-brand-50/80 to-white p-6 rounded-xl border border-brand-100 shadow-sm mb-8">
                        <h4 className="text-xs font-bold text-brand-800 uppercase tracking-wider mb-4">Uw voordelen</h4>
                        <ul className="space-y-4">
                          <li className="flex items-start gap-3 text-slate-800">
                            <div className="mt-1 w-5 h-5 bg-brand-100 rounded-full flex items-center justify-center text-brand-600 shrink-0">
                                <Check size={12} strokeWidth={3} />
                            </div>
                            <span className="leading-snug">Wij claimen uw <strong>smartengeld</strong> en <strong>inkomensverlies</strong>.</span>
                          </li>
                          <li className="flex items-start gap-3 text-slate-800">
                             <div className="mt-1 w-5 h-5 bg-brand-100 rounded-full flex items-center justify-center text-brand-600 shrink-0">
                                <Check size={12} strokeWidth={3} />
                            </div>
                            <span className="leading-snug">De tegenpartij betaalt onze juridische kosten.</span>
                          </li>
                          <li className="flex items-start gap-3 text-slate-800">
                             <div className="mt-1 w-5 h-5 bg-brand-100 rounded-full flex items-center justify-center text-brand-600 shrink-0">
                                <Check size={12} strokeWidth={3} />
                            </div>
                            <span className="leading-snug">Geen juridisch jargon, maar heldere taal.</span>
                          </li>
                        </ul>
                      </div>

                      <div className="flex flex-col gap-4">
                        <Button 
                            size="lg" 
                            fullWidth 
                            className="bg-brand-700 hover:bg-brand-800 text-lg shadow-lg shadow-brand-900/10"
                            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                        >
                            Claim uw gratis intake <ChevronRight className="ml-1" size={20} />
                        </Button>
                        
                        {/* DEMO INSIGHT BLOCK */}
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-start gap-3 mt-2">
                            <Lightbulb size={16} className="text-slate-400 mt-0.5 shrink-0" />
                            <p className="text-xs text-slate-500 leading-relaxed">
                              <span className="font-semibold text-slate-700">Demo Inzicht:</span> In een live omgeving wordt dit resultaat direct gekoppeld aan uw CRM. Uw team ontvangt enkel kansrijke, geprekwalificeerde dossiers.
                            </p>
                        </div>

                        <div className="text-center mt-2">
                           <button onClick={resetWizard} className="text-xs text-slate-400 hover:text-slate-600 underline decoration-slate-300 underline-offset-2">
                              Opnieuw beginnen
                           </button>
                        </div>
                      </div>

                      {/* Sales Hint added here */}
                      <FixelsHint />
                    </div>
                  )}
                </motion.div>
              )}

            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};